/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import { useTranslation } from "react-i18next";
import PAMediaRemovalHeader from "./partials/PAMediaRemovalHeader";
import { useEffect, useState } from "react";
import {
	PAMediaBatch,
	PAMediaPlateData,
	PAMediaRemovalBatchListResponse,
	PAMediaRemovalMethod,
	PAMediaRemovalMethodResponse,
} from "./mediaRemoval.types";
import { postAutoclaveServices } from "../../../../service/postAutoclaveServices";
import PAMediaInfoCard from "./partials/PAMediaInfoCard";
import MediaPrepTable from "../../../common/commonComponents/lotDetailsTable";
import getPAMediaPlateColumns from "./partials/columns";
import PAConfirmModal from "./partials/PAConfirmModal";
import PASuccessModal from "./partials/PASuccessModal";
import CustomToast from "../../../../widgets/toaster";

const MediaRemoval = () => {
	const { t } = useTranslation();
	const [mediaBatches, setMediaBatches] = useState<PAMediaBatch[]>([]);
	const [containers, setContainers] = useState<PAMediaPlateData[]>([]);
	const [selectedBatch, setSelectedBatch] = useState<PAMediaBatch>();
	const [totalElements, setTotalElements] = useState(0);
	const [allRemoved, setAllRemoved] = useState(false);
	const [removalMethodId, setRemovalMethodId] = useState("");
	const [remarks, setRemarks] = useState("");
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [selectedContainerIds, setSelectedContainerIds] = useState<string[]>(
		[],
	);
	const [removalMethods, setRemovalMethods] = useState<PAMediaRemovalMethod[]>(
		[],
	);
	const [useEffectTrigger, setUseEffectTrigger] = useState(false);
	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});

	const [errors, setErrors] = useState({
		batch: "",
		plates: "",
		allRemoved: "",
		removalMethod: "",
	});

	const validateFields = () => {
	const newErrors = {
		batch: "",
		plates: "",
		allRemoved: "",
		removalMethod: "",
	};

	let valid = true;

	if (!selectedBatch) {
		newErrors.batch = t("postAutoclaveProcess.errors.batch");
		valid = false;
	}

	if (selectedContainerIds.length === 0) {
		newErrors.plates = t("postAutoclaveProcess.errors.plates");
		valid = false;
	}

	if (!allRemoved) {
		newErrors.allRemoved = t("postAutoclaveProcess.errors.allRemoved");
		valid = false;
	}

	if (!removalMethodId) {
		newErrors.removalMethod = t("postAutoclaveProcess.errors.removalMethod");
		valid = false;
	}

	setErrors(newErrors);
	return valid;
	};

	const getMediaBatchList = async () => {
		try {
			const urlParams = {};
			const response: PAMediaRemovalBatchListResponse =
				await postAutoclaveServices.getMediaBatches(urlParams);
			setMediaBatches(response.data);
			setSelectedBatch(undefined); // Don't preselect any batch
		} catch (error) {
			console.error(error);
		}
	};

	const getMediaRemovalMethods = async () => {
		try {
			const response: PAMediaRemovalMethodResponse =
				await postAutoclaveServices.getMediaRemovalMethods();
			setRemovalMethods(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getAutoclavePlates = async () => {
		if (!selectedBatch) return;
		try {
			const urlParams = {
				sort: "id",
				order: "desc",
				batch_number: selectedBatch.media_batch_identifier,
			};
			const response = await postAutoclaveServices.getContainers(urlParams);
			setContainers(response.data);
			setTotalElements(Number(response.totalElements));
		} catch (error) {
			console.error(error);
		}
	};

	const handleSelectContainer = (containerId: string) => {
		setErrors((prev) => ({
			...prev,
			plates: "",
		}));

		setSelectedContainerIds((prev) =>
			prev.includes(containerId)
				? prev.filter((id) => id !== containerId)
				: [...prev, containerId]
		);
	};

	const handleSuccess = () => {
		setUseEffectTrigger((prev) => !prev);
		setAllRemoved(false);
		setRemovalMethodId("");
		setRemarks("");
	};

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	useEffect(() => {
		getMediaBatchList();
		getMediaRemovalMethods();
	}, []);

	useEffect(() => {
		setSelectedContainerIds([]);
		if (!selectedBatch) return;

		getAutoclavePlates();
	}, [selectedBatch, useEffectTrigger]);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("postAutoclaveProcess.title")} pageDescription={t('pageDescription.postAutoclave.description')}/>
			<div className="flex flex-col h-full bg-[#F3F6F9] relative">
				<PAMediaRemovalHeader />
				<div className="p-4 flex-1 overflow-y-auto">
					<div className="max-w-4xl mx-auto space-y-3">
						<div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 space-y-6">
							<div className="flex items-center gap-3 mb-2">
								<span className="material-symbols-outlined text-[#FF7043] text-xl">
									search
								</span>
								<h3 className="text-sm font-black text-[#2D3E50] uppercase tracking-widest">
									{t(
										"postAutoclaveProcess.mediaRemoval.mediaBatchSelection",
									)}
								</h3>
							</div>
							<div className="space-y-4">
								<div className="space-y-2">
									<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
										{t(
											"postAutoclaveProcess.mediaRemoval.selectMediaBatchPlates",
										)}{" "}
										<span className="text-red-500">*</span>
									</label>
									<select
										value={selectedBatch?.media_batch_identifier ?? ""}
										onChange={(e) => {
											const selected = mediaBatches.find(
												(batch) => batch.media_batch_identifier === e.target.value
											);
											setSelectedBatch(selected);
											setErrors((prev) => ({
												...prev,
												batch: "",
											}));
										}}
										className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
									>
										<option value="" disabled>
											{t("postAutoclaveProcess.mediaRemoval.selectABatch")}
										</option>

										{mediaBatches.map((batch) => (
											<option
												key={batch.media_batch_identifier}
												value={batch.media_batch_identifier}
											>
												{batch.media_batch_identifier} ({batch.media_batch_description})
											</option>
										))}
									</select>
									{errors.batch && (
										<p className="mt-1 text-[11px] text-red-500">
											{errors.batch}
										</p>
									)}
									<p className="text-[10px] text-slate-400 italic">
										{t(
											"postAutoclaveProcess.mediaRemoval.onlyBatchesWithCompleted",
										)}
									</p>
								</div>

								<div className="grid grid-cols-3 gap-6">
									<PAMediaInfoCard
										label={t(
											"postAutoclaveProcess.mediaRemoval.activity",
										)}
										value={selectedBatch?.media_batch_description}
									/>
									{/* <PAMediaInfoCard
										label={t(
											"postAutoclaveProcess.mediaRemoval.autoclaveIdAndCycle",
										)}
									/> */}
									<PAMediaInfoCard
										label={t(
											"postAutoclaveProcess.mediaRemoval.numberOfPlates",
										)}
										value={String(containers.length)}
									/>
								</div>
							</div>
						</div>
						<div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
							<div className="px-8 py-5 border-b border-slate-50 flex justify-between items-center bg-slate-50/30">
								<div className="flex items-center gap-3">
									<span className="material-symbols-outlined text-[#FF7043] text-xl">
										list_alt
									</span>
									<h3 className="text-sm font-black text-[#2D3E50] uppercase tracking-widest">
										{t(
											"postAutoclaveProcess.mediaRemoval.platesForRemoval",
										)}
									</h3>
								</div>
								<span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-[10px] font-black uppercase tracking-wider">
									{t(
										"postAutoclaveProcess.mediaRemoval.totalSelection",
									)}
									: {selectedContainerIds.length} / {totalElements}
								</span>
							</div>
							<MediaPrepTable
								columns={getPAMediaPlateColumns(
									t,
									handleSelectContainer,
								)}
								data={containers}
								emptyMessage={t(
									"postAutoclaveProcess.mediaRemoval.noPlates",
								)}
							/>
							{errors.plates && (
								<div className="px-8 py-2">
									<p className="text-[11px] text-red-500">
										{errors.plates}
									</p>
								</div>
							)}
						</div>
						<div className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
							<input
								type="checkbox"
								id="all-removed"
								checked={allRemoved}
								onChange={(e) => {
									setAllRemoved(e.target.checked);
									setErrors((prev) => ({
										...prev,
										allRemoved: "",
									}));
								}}
								className="mt-1 w-5 h-5 rounded border-slate-300 text-[#FF7043] focus:ring-[#FF7043]"
							/>
							<div className="space-y-1">
								<label
									htmlFor="all-removed"
									className="text-sm font-black text-[#2D3E50]"
								>
									{t(
										"postAutoclaveProcess.mediaRemoval.mediaRemovedFromAllPlates",
									)}  <span className="text-red-500">*</span>
								</label>
								<p className="text-[10px] text-slate-400 leading-relaxed">
									{t(
										"postAutoclaveProcess.mediaRemoval.mandatoryVerification",
									)}
								</p>
								{errors.allRemoved && (
								<div className="px-0">
									<p className="text-[11px] text-red-500">
										{errors.allRemoved}
									</p>
								</div>
							)}

							</div>
						</div>
						<div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 space-y-4">
							<div className="space-y-2">
								<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
									{t("postAutoclaveProcess.mediaRemoval.removalMethod",)}
									<span className="text-red-500">*</span>
								</label>
								<select
									value={removalMethodId}
									onChange={(e) => {
										setRemovalMethodId(e.target.value);
										setErrors((prev) => ({
											...prev,
											removalMethod: "",
										}));
									}}
									className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
								>
									<option value="">
										{t(
											"postAutoclaveProcess.mediaRemoval.selectRemovalMethod",
										)}
									</option>
									{removalMethods.map((method) => (
										<option key={method.id} value={method.id}>
											{method.name}
										</option>
									))}
								</select>
								{errors.removalMethod && (
									<p className="mt-1 text-[11px] text-red-500">
										{errors.removalMethod}
									</p>
								)}
							</div>

							<div className="space-y-2">
								<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
									{t(
										"postAutoclaveProcess.mediaRemoval.remarksOptional",
									)}
								</label>
								<textarea
									placeholder={t(
										"postAutoclaveProcess.mediaRemoval.remarksPlaceHolder",
									)}
									onChange={(e) => setRemarks(e.target.value)}
									className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all h-24 resize-none"
								></textarea>
							</div>
							{/* Action Buttons */}
							<div className="flex justify-end gap-4 pt-4">
								{/* <button className="px-8 py-3 text-xs font-black text-slate-500 uppercase tracking-widest hover:text-slate-800 transition-all border-none rounded-xl">
									{t("postAutoclaveProcess.mediaRemoval.cancel")}
								</button> */}
								<button
									onClick={() => {
										if (!validateFields()) return;
										setShowConfirmModal(true);
									}}
									className="px-10 py-3 bg-[#2D3E50] text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-slate-200 hover:bg-slate-800 transition-all"
								>
									{t(
										"postAutoclaveProcess.mediaRemoval.confirmAndProceed",
									)}
								</button>
							</div>
						</div>
						{showConfirmModal && (
							<PAConfirmModal
								showToast={showToast}
								selectedContainersId={selectedContainerIds}
								remarks={remarks}
								removalMethodId={removalMethodId}
								mediaBatchId={selectedBatch?.id ?? ""}
								mediaBatchName={selectedBatch?.media_batch_identifier}
								toggleConfirmModal={() => setShowConfirmModal(false)}
								showSuccessModal={() => setShowSuccessModal(true)}
								handleSuccess={handleSuccess}
							/>
						)}
						{showSuccessModal && (
							<PASuccessModal
								toggleSuccessModal={() => setShowSuccessModal(false)}
							/>
						)}
					</div>
				</div>
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={400}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default MediaRemoval;
